import { useMemo, useState } from "react";
import { ExportModal } from "@/components";
import {
  ApiResponse,
  axiosInstance,
  createCsvContent,
  downloadCsvFile,
  handleAxiosError,
} from "@/helpers";
import { ISSUE_EXPORT_COLUMNS, getIssueExportColumnValue } from "../../_constants/issues-export.const";
import type {
  Issue,
  IssueExportColumnKey,
  IssuePeriod,
  IssueStatus,
  IssueTypeFilter,
} from "../../_types/issues.types";

type IssueExportModalProps = {
  isOpen: boolean;
  type: IssueTypeFilter;
  period: IssuePeriod;
  status: IssueStatus;
  onClose: () => void;
};

export const IssueExportModal = ({
  isOpen,
  type,
  period,
  status,
  onClose,
}: IssueExportModalProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [selectedExportColumns, setSelectedExportColumns] = useState<Array<IssueExportColumnKey>>(
    () =>
      ISSUE_EXPORT_COLUMNS.filter((column) => column.defaultSelected).map(
        (column) => column.key
      ) as Array<IssueExportColumnKey>
  );

  const exportOptions = useMemo(
    () =>
      ISSUE_EXPORT_COLUMNS.map((column) => ({
        key: column.key,
        label: column.label,
        description: column.description,
      })),
    []
  );

  const handleClose = () => {
    if (isExporting) return;
    setExportError(null);
    onClose();
  };

  const handleToggleExportColumn = (key: string, checked: boolean) => {
    setExportError(null);
    setSelectedExportColumns((currentColumns) => {
      const typedKey = key as IssueExportColumnKey;

      if (checked) {
        return currentColumns.includes(typedKey)
          ? currentColumns
          : [...currentColumns, typedKey];
      }

      return currentColumns.filter((columnKey) => columnKey !== typedKey);
    });
  };

  const handleSelectAllExportColumns = () => {
    setExportError(null);
    setSelectedExportColumns(ISSUE_EXPORT_COLUMNS.map((column) => column.key));
  };

  const handleClearExportColumns = () => {
    setExportError(null);
    setSelectedExportColumns([]);
  };

  const handleExportIssues = async () => {
    if (!selectedExportColumns.length) {
      setExportError("Select at least one field to export.");
      return;
    }

    try {
      setIsExporting(true);
      setExportError(null);

      const response = await axiosInstance.get<ApiResponse<Array<Issue>>>(
        "/v2/issues/export",
        {
          params: {
            type,
            period,
            status,
          },
        }
      );

      const columns = ISSUE_EXPORT_COLUMNS.filter((column) =>
        selectedExportColumns.includes(column.key)
      );
      const csvContent = createCsvContent(
        columns.map((column) => column.label),
        (response.data.data ?? []).map((issue) =>
          columns.map((column) => getIssueExportColumnValue(issue, column))
        )
      );

      downloadCsvFile(
        `issues-${new Date().toISOString().slice(0, 10)}.csv`,
        csvContent
      );

      onClose();
    } catch (error) {
      setExportError(
        handleAxiosError(error)?.message ?? "Failed to export issues. Please try again."
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <ExportModal
      isOpen={isOpen}
      title="Export issues"
      description="Choose which issue fields should be included in the CSV export."
      options={exportOptions}
      selectedKeys={selectedExportColumns}
      isExporting={isExporting}
      errorMessage={exportError}
      onClose={handleClose}
      onToggleOption={handleToggleExportColumn}
      onSelectAll={handleSelectAllExportColumns}
      onClearAll={handleClearExportColumns}
      onExport={handleExportIssues}
    />
  );
};
